import { Link } from 'react-router-dom';
import type { StatusData } from '../page';

interface Props { data: StatusData; }

const STATUS_CFG: Record<string, { label: string; badge: string; dot: string }> = {
  pending:   { label: 'Pending Review', badge: 'bg-amber-50 text-amber-700 border-amber-200',    dot: 'bg-amber-400' },
  approved:  { label: 'DNS Setup',      badge: 'bg-sky-50 text-sky-700 border-sky-200',           dot: 'bg-sky-400' },
  active:    { label: 'Active',         badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-400 animate-pulse' },
  rejected:  { label: 'Rejected',       badge: 'bg-red-50 text-red-600 border-red-200',           dot: 'bg-red-400' },
  cancelled: { label: 'Cancelled',      badge: 'bg-slate-100 text-slate-500 border-slate-200',    dot: 'bg-slate-300' },
};

export default function PublicSitesTab({ data }: Props) {
  const totalDomainReqs = data.domainRequestsPending + data.domainRequestsApproved +
                          data.domainRequestsActive + data.domainRequestsRejected;

  return (
    <div className="space-y-6">

      {/* ── Stats ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Active Studios',     value: data.tenantsActive,           icon: 'ri-global-line',          color: 'text-blue-600 bg-blue-50' },
          { label: 'Public Sites',       value: data.tenantsWithSlug,         icon: 'ri-layout-masonry-line',  color: 'text-emerald-600 bg-emerald-50' },
          { label: 'Custom Domains',     value: data.domainRequestsActive,    icon: 'ri-earth-line',           color: 'text-violet-600 bg-violet-50' },
          { label: 'Pending DNS',        value: data.domainRequestsPending + data.domainRequestsApproved, icon: 'ri-time-line', color: 'text-amber-600 bg-amber-50' },
        ].map((s) => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
            <span className={`w-10 h-10 flex items-center justify-center rounded-xl shrink-0 ${s.color.split(' ')[1]}`}>
              <i className={`${s.icon} text-base ${s.color.split(' ')[0]}`}></i>
            </span>
            <div>
              <p className="text-slate-400 text-xs uppercase tracking-wider">{s.label}</p>
              <p className="text-slate-900 text-2xl font-bold">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* ── Public site infrastructure ── */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2">
            <i className="ri-global-line text-slate-400 text-sm"></i>
            <h3 className="text-slate-700 text-sm font-semibold">Public Site Infrastructure</h3>
          </div>
          <div className="px-5 py-1">
            {[
              {
                icon: 'ri-layout-masonry-line',
                name: 'Tenant Public Pages (/s/:slug)',
                desc: 'Each tenant has a public-facing studio page',
                level: 'active',
                note: `${data.tenantsWithSlug} active sites`,
              },
              {
                icon: 'ri-image-2-line',
                name: 'Photo Gallery Portal',
                desc: 'Client selection & delivery portal',
                level: 'active',
                note: 'Live at /portal/:token',
              },
              {
                icon: 'ri-mail-open-line',
                name: 'Digital Invitations',
                desc: 'Public invitation pages',
                level: 'active',
                note: 'Live at /invite/:slug',
              },
              {
                icon: 'ri-disc-line',
                name: 'Media Delivery Pages',
                desc: 'Online DVD / media packages',
                level: 'active',
                note: 'Live at /media/:token',
              },
              {
                icon: 'ri-earth-line',
                name: 'Custom Domain Routing',
                desc: 'Proxy domains to tenant slugs',
                level: 'pending',
                note: 'DNS / reverse proxy not configured',
              },
              {
                icon: 'ri-robot-line',
                name: 'AI Chat Widget',
                desc: 'Embedded chat on public pages',
                level: 'pending',
                note: 'Backend not deployed',
              },
            ].map((item) => (
              <div key={item.name} className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-100 shrink-0">
                  <i className={`${item.icon} text-slate-400 text-xs`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-slate-700 text-xs font-medium">{item.name}</p>
                  <p className="text-slate-400 text-[10px]">{item.desc}</p>
                  {item.note && <p className="text-slate-400 text-[10px] italic">{item.note}</p>}
                </div>
                <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold whitespace-nowrap shrink-0 ${
                  item.level === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${item.level === 'active' ? 'bg-emerald-400 animate-pulse' : 'bg-slate-300'}`}></span>
                  {item.level === 'active' ? 'Active' : 'Pending'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Custom domain requests ── */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="ri-earth-line text-slate-400 text-sm"></i>
              <h3 className="text-slate-700 text-sm font-semibold">Custom Domain Requests</h3>
            </div>
            {totalDomainReqs > 0 && (
              <Link to="/sa/domain-requests" className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-xs font-medium transition-colors">
                Manage <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            )}
          </div>
          <div className="p-5">
            {totalDomainReqs === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-3">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-slate-100">
                  <i className="ri-earth-line text-slate-300 text-2xl"></i>
                </div>
                <p className="text-slate-400 text-sm text-center">No domain requests yet</p>
                <p className="text-slate-400 text-xs text-center">Tenants can request custom domains from their Admin Panel.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Request breakdown */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'pending',  label: 'Pending',     value: data.domainRequestsPending },
                    { key: 'approved', label: 'DNS Setup',   value: data.domainRequestsApproved },
                    { key: 'active',   label: 'Active',      value: data.domainRequestsActive },
                    { key: 'rejected', label: 'Rejected',    value: data.domainRequestsRejected },
                  ].map((s) => {
                    const cfg = STATUS_CFG[s.key];
                    return (
                      <div key={s.key} className={`flex items-center gap-2.5 p-3 rounded-xl border ${cfg.badge}`}>
                        <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot} shrink-0`}></span>
                        <div>
                          <p className="text-xl font-bold">{s.value}</p>
                          <p className="text-[10px] font-medium opacity-70">{s.label}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Lifecycle explanation */}
                <div className="mt-2 pt-3 border-t border-slate-100">
                  <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">Lifecycle</p>
                  <div className="flex items-center gap-1 text-xs text-slate-400 flex-wrap">
                    {['pending', 'approved', 'active'].map((s, i, arr) => (
                      <>
                        <span key={s} className={`px-2 py-0.5 rounded-full border font-medium ${STATUS_CFG[s].badge}`}>
                          {STATUS_CFG[s].label}
                        </span>
                        {i < arr.length - 1 && <i key={`arrow-${s}`} className="ri-arrow-right-line text-slate-300 text-xs"></i>}
                      </>
                    ))}
                  </div>
                  <p className="text-slate-400 text-[10px] mt-2 leading-relaxed">
                    Pending → SA approves → DNS records sent to tenant → DNS verified → Active.
                    Full DNS backend integration is pending configuration.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── DNS backend note ── */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <i className="ri-error-warning-line text-amber-500 text-lg shrink-0 mt-0.5"></i>
        <div>
          <p className="text-amber-900 text-sm font-semibold">Custom Domains — Backend Pending</p>
          <p className="text-amber-700 text-xs mt-1 leading-relaxed">
            The request/approval workflow is complete. What's missing: a reverse proxy (Caddy / Nginx / Cloudflare) to map approved domains to tenant slugs, and a DNS verification checker (Edge Function) to confirm propagation before marking as <strong>Active</strong>.
          </p>
        </div>
      </div>
    </div>
  );
}
